import React, { createContext, useState, useEffect } from 'react';
import ssoService from '../services/ssoService';
import { isSuperAdmin } from '../utils/superAdminChecker';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authMode, setAuthMode] = useState(null); // 'sso' or 'standalone'

  // Initialize authentication state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // Check if we have an SSO token from URL params or postMessage
      const urlParams = new URLSearchParams(window.location.search);
      const ssoToken = urlParams.get('ssoToken') || urlParams.get('token');

      // If embedded (has ssoToken or embed param), wait a bit for postMessage
      const isEmbedded = urlParams.get('embed') === 'true' || ssoToken;

      if (isEmbedded && ssoToken) {
        console.log('🔐 Detected SSO token, attempting SSO login...');

        // Wait a bit for postMessage to arrive with user data
        await new Promise(resolve => setTimeout(resolve, 500));

        // Attempt SSO login
        const result = await loginWithSSO();
        if (result.success) {
          console.log('✅ SSO login successful');
          setLoading(false);
          return;
        }
      }

      // Fallback to stored credentials
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        setIsAuthenticated(true);
        setAuthMode(parsedUser.authMode || 'sso');

        // Refresh user profile from server to pick up feature flag changes
        // (e.g. niraiBooks enabled by SuperAdmin after last login)
        _refreshProfile(storedToken);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const _refreshProfile = async (authToken) => {
    try {
      const res = await fetch('/api/auth/profile', {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && data.user) {
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
    } catch {
      // silent — cached data stays in use
    }
  };

  const login = (userData, authToken, mode = 'standalone') => {
    setUser(userData);
    setToken(authToken);
    setIsAuthenticated(true);
    setAuthMode(mode);

    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));

    // Fetch fresh profile so integration flags (niraiBooks, koorHRMS) are
    // available immediately — login response doesn't include company-level flags.
    _refreshProfile(authToken);
  };

  const loginWithSSO = async () => {
    try {
      const result = await ssoService.completeSSOLogin();

      if (result.success) {
        login(result.user, result.token, 'sso');
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('SSO login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    setAuthMode(null);

    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  const value = {
    isAuthenticated,
    user,
    token,
    authMode,
    loading,
    isSuperAdmin: user ? isSuperAdmin(user) : false,
    login,
    loginWithSSO,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
